import { useEffect, useMemo, useRef } from "react";

import html2canvas from "html2canvas";

import Sentry from "@similarweb/sentry-logger";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import ChartStreamService from "app/pages/sales-intelligence/modules/ai-agent/services/chart/chartService";
import useEnrichedRecents from "app/pages/sales-intelligence/modules/company-analysis/hooks/useEnrichedRecents";
import { AIAgentEmailGraphContainer } from "app/pages/sales-intelligence/modules/ai-agent/containers/AIAgentEmailGraph/AIAgentEmailGraphContainer";
import { AIAgentEmailPreview } from "app/pages/sales-intelligence/pages/ai-agent/containers/AIAgentEmailPreview/AIAgentEmailPreview";
import { LowQuotaBannerContainer } from "app/pages/sales-intelligence/modules/quota/containers/LowQuotaBannerContainer/LowQuotaBannerContainer";
import { AIAgentEmailRefinePanelWithErrorBoundary } from "app/pages/sales-intelligence/pages/ai-agent/containers/AIAgentEmailRefinePanel/AIAgentEmailRefinePanelWithErrorBoundary";
import AIEmailAgentToolbar from "app/pages/sales-intelligence/pages/ai-agent/containers/AIAgentEmailToolbar/AIAgentEmailToolbar";
import useEmailAgentDefaultContext from "app/pages/sales-intelligence/pages/ai-agent/hooks/useEmailAgentDefaultContext";
import { useEmailComposer } from "app/pages/sales-intelligence/pages/ai-agent/hooks/useEmailComposer";

export const AIAgentEmailPageContent = () => {
    const { recentUniqueCompanies } = useEnrichedRecents();

    const {
        recipient,
        selectedCompany,
        activeCompany,
        subject,
        body,
        chart,
        chartAnchorIndex,
        setChartAnchorIndex,
        emailPreviews,
        selectablePreviews,
        isLoading,
        hasPayload,
        setRecipient,
        onCompanySelect,
        setSubject,
        setBody,
        onGenerate,
        isGenerateAvailable,
        threadId,
        messageId,
        graphImg,
        setGraphImg,
        versions,
        onAddVersion,
        isUpdatingVersion,
        setIsUpdatingVersion,
        onStop,
        onSetEmailPreviews,
    } = useEmailComposer();

    useEmailAgentDefaultContext(onCompanySelect, setRecipient, onGenerate);

    const graphRef = useRef<HTMLDivElement>(null);

    const makeImg = async () => {
        const el = graphRef.current;
        if (!el) {
            return;
        }

        try {
            const canvas = await html2canvas(el, { backgroundColor: "#fff", useCORS: true });

            if (canvas.width === 0 || canvas.height === 0) {
                Sentry.error("Generated canvas has invalid dimensions", new Error("Invalid canvas dimensions"));
                return;
            }

            const dataUrl = canvas.toDataURL("image/png");

            if (!dataUrl || !dataUrl.startsWith("data:image/png;base64,")) {
                Sentry.error("Failed to generate valid image data URL", new Error("Invalid image data URL"));
                return;
            }

            setGraphImg(dataUrl);
        } catch (error) {
            Sentry.error("Failed to generate image:", error);
        } finally {
            setIsUpdatingVersion(false);
        }
    };

    useEffect(() => {
        if (chart) {
            void makeImg();
        } else {
            setGraphImg("");
        }
    }, [chart]);

    const handleBodyChange = (v: string, imageIndex: number) => {
        setBody(v);
        setChartAnchorIndex(imageIndex);
    };

    const isInitialLoading = isLoading && !subject && !body;
    const isCompareChart = useMemo(
        () => !!chart && ChartStreamService.isChartObject(chart) && ChartStreamService.isCompareChart(chart),
        [chart],
    );
    const isChartValid = useMemo(() => !!chart && ChartStreamService.isChartObject(chart), [chart]);

    const competitors = useMemo(() => ChartStreamService.getChartCompetitors(chart), [chart]);

    return (
        <>
            <AIEmailAgentToolbar
                recents={recentUniqueCompanies}
                selected={selectedCompany}
                onSelect={onCompanySelect}
                recipient={recipient}
                onRecipientChange={setRecipient}
                onGenerate={onGenerate}
                isSubmitButtonDisabled={!isGenerateAvailable}
            />
            <LowQuotaBannerContainer isInline />
            <Box
                position="fixed"
                top="-10000px"
                left="-10000px"
                zIndex="-1"
                aria-hidden
                ref={graphRef}
            >
                {isChartValid ? (
                    <AIAgentEmailGraphContainer
                        key={ChartStreamService.getChartKey(chart)}
                        chart={chart}
                        selectedCompany={activeCompany}
                    />
                ) : null}
            </Box>
            <Flex
                flex="1"
                gap="24px"
                maxHeight="100%"
                overflow="hidden"
            >
                {hasPayload && !isInitialLoading ? (
                    <AIAgentEmailRefinePanelWithErrorBoundary
                        isUpdatingVersion={isUpdatingVersion}
                        isLoading={Boolean(isLoading && !emailPreviews.length)}
                        selectablePreviews={selectablePreviews}
                        selectedCompany={activeCompany}
                        versions={versions}
                        onAddVersion={onAddVersion}
                        threadId={threadId}
                        onEmailGenerationStop={onStop}
                        competitors={competitors}
                        onSetEmailPreviews={onSetEmailPreviews}
                    />
                ) : null}
                <AIAgentEmailPreview
                    isInitialLoading={isInitialLoading}
                    isUpdatingVersion={isUpdatingVersion}
                    isLoading={isLoading}
                    subject={subject}
                    body={body}
                    recipient={recipient}
                    onSubjectChange={setSubject}
                    onBodyChange={handleBodyChange}
                    hasPayload={hasPayload}
                    chartAnchorIndex={chartAnchorIndex}
                    selectedCompany={activeCompany}
                    threadId={threadId}
                    messageId={messageId}
                    graphImg={graphImg}
                    isCompareChart={isCompareChart}
                />
            </Flex>
        </>
    );
};
